import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine, scrollToId, EASE } from "./motion";
import { useT } from "@/i18n/LangContext";

const HERO_IMG = "/images/hero.jpg";

const BeeAsterisk = () => (
  <div style={{ perspective: 600 }} aria-hidden="true">
    <motion.div
      animate={{ rotateY: [0, 360] }}
      transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
      style={{ transformStyle: "preserve-3d" }}
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="relative h-28 w-28 md:h-36 md:w-36"
      >
        {[0, 60, 120].map((deg) => (
          <span
            key={deg}
            className="absolute left-1/2 top-1/2 h-full w-3.5 rounded-full bg-bee shadow-[0_8px_32px_rgba(255,199,44,0.45)] md:w-4"
            style={{ transform: `translate(-50%, -50%) rotate(${deg}deg)` }}
          />
        ))}
      </motion.div>
    </motion.div>
  </div>
);

export default function Hero() {
  const t = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const shapeY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pb-24 pt-36 md:pb-36 md:pt-48"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 md:grid-cols-12 md:px-10">
        <div className="md:col-span-7">
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
            className="mb-8 text-xs font-medium uppercase tracking-[0.25em] text-ink/75 md:text-sm"
          >
            {t.hero.overline}
          </motion.p>
          <h1
            data-testid="hero-heading"
            className="font-serif text-5xl font-light leading-[0.98] tracking-tighter text-ink md:text-7xl lg:text-[5.6rem]"
          >
            <MaskedLine delay={0.15}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.3}>
              {t.hero.line2} <em className="font-normal">{t.hero.line2Em}</em>
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="relative inline-block">
                {t.hero.line3}
                <span
                  className="absolute -bottom-1 left-0 h-[0.05em] w-full bg-bee md:-bottom-2"
                  aria-hidden="true"
                />
              </span>
              <span className="text-bee">.</span>
            </MaskedLine>
          </h1>
          <motion.p
            data-testid="hero-value-statement"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-10 max-w-xl text-lg font-normal leading-relaxed text-ink md:text-xl"
          >
            {t.hero.value}
          </motion.p>
          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.82, ease: EASE }}
            className="mt-5 max-w-xl text-base font-light leading-relaxed text-ink/85"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-12 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-5"
          >
            <a
              href="#contact"
              data-testid="hero-request-call-button"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#contact");
              }}
              className="group flex min-h-[52px] w-full items-center justify-center gap-3 rounded-full bg-ink px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-bee hover:text-ink sm:w-auto"
            >
              {t.hero.ctaCall}
              <ArrowUpRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                aria-hidden="true"
              />
            </a>
            <a
              href="#services"
              data-testid="hero-explore-button"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#services");
              }}
              className="group flex min-h-[52px] w-full items-center justify-center gap-3 rounded-full border border-ink/25 px-8 py-4 text-sm font-medium text-ink transition-colors duration-300 hover:border-bee hover:bg-lav sm:w-auto"
            >
              {t.hero.ctaExplore}
              <ArrowDown
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1"
                aria-hidden="true"
              />
            </a>
          </motion.div>
        </div>

        <div className="relative md:col-span-5">
          <motion.div
            style={{ y: imgY }}
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
            className="relative overflow-hidden rounded-t-[12rem] border border-lavline"
            data-testid="hero-image-frame"
          >
            <img
              src={HERO_IMG}
              alt={t.hero.imgAlt}
              className="aspect-[3/4] w-full scale-105 object-cover"
              loading="eager"
            />
          </motion.div>
          <motion.div
            style={{ y: shapeY }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 1.1, ease: EASE }}
            className="absolute -bottom-8 -left-8 md:-left-14"
            data-testid="hero-bee-asterisk"
          >
            <BeeAsterisk />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
